/**
 * Una entrada del menú: fila suelta (con ícono) o fila dentro de un espacio.
 *
 * 🔴 El estado activo viaja por la prop `isActive` del primitivo, no por la
 * función `className` del `NavLink`: con `asChild`, el `Slot` concatena el
 * className del padre con el del hijo ANTES de que el NavLink lo evalúe, y la
 * función terminaba serializada como texto dentro del atributo `class`. El
 * tratamiento visual lo aporta `[data-active='true']` desde `index.css`.
 */
import { NavLink } from 'react-router-dom';

import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar';
import { NAV_META } from '@/copy/nav';

import { type Contadores, contadorDe } from './contadores';
import type { Entrada } from './registro';

export function ContadorDelMenu({ cantidad }: { cantidad: number }) {
  if (cantidad <= 0) return null;
  return (
    <span className="bg-primary text-primary-foreground ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 font-mono text-micro font-semibold">
      {cantidad}
    </span>
  );
}

function Atajo({ kbd }: { kbd?: string }) {
  if (!kbd) return null;
  return (
    <span className="text-fg-dim group-data-[active=true]/nav:text-brand ml-auto font-mono text-micro font-medium group-data-[collapsible=icon]:hidden">
      {kbd}
    </span>
  );
}

interface Props {
  entrada: Entrada;
  activa: boolean;
  contadores: Contadores;
  /** Dentro de un espacio: sin ícono y con el primitivo de sub-menú. */
  anidada?: boolean;
  onElegir: () => void;
}

export function EntradaDelMenu({ entrada, activa, contadores, anidada = false, onElegir }: Props) {
  const meta = NAV_META[entrada.id];
  const cantidad = contadorDe(entrada, contadores);
  const Icono = entrada.icon;
  const enlace = (
    <NavLink
      to={entrada.to}
      end={entrada.to === '/dashboard'}
      onClick={onElegir}
      className="group/nav relative border-0 font-medium hover:bg-surface-hi/55"
    >
      {Icono && (
        <Icono className="text-fg-muted shrink-0 transition-colors group-data-[active=true]/nav:text-brand" />
      )}
      <span>{meta?.label ?? entrada.id}</span>
      <ContadorDelMenu cantidad={cantidad} />
      <Atajo kbd={entrada.kbd} />
    </NavLink>
  );

  if (anidada) {
    return (
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild isActive={activa} title={meta?.description}>
          {enlace}
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={activa}
        tooltip={{
          children: (
            <div className="max-w-[200px]">
              <p className="font-medium">{meta?.label ?? entrada.id}</p>
              {meta?.description && (
                <p className="mt-0.5 text-micro leading-snug opacity-80">{meta.description}</p>
              )}
            </div>
          ),
        }}
      >
        {enlace}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
